import { useMemo, useState } from 'react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DisabledHint } from '@/components/common/DisabledHint';
import { ErrorState } from '@/components/common/ErrorState';
import { ExportButton } from '@/components/common/ExportButton';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { downloadLeaveReport, leaveReportListInfiniteQueryOptions } from '../api/leaveReport.api';
import { LeaveReportDatesDialog } from '../components/LeaveReportDatesDialog';
import { LeaveReportFilterPanel } from '../components/LeaveReportFilterPanel';
import {
  getLeaveReportColumns,
  LEAVE_REPORT_GROUPED_COLUMNS,
} from '../components/LeaveReportColumns';
import { useLeaveReportFilters } from '../hooks/useLeaveReportFilters';
import type { LeaveReportDateSelection, LeaveReportParams } from '../types/leaveReport.types';

const EXPORT_ARIA_LABEL = 'Download Excel';
const NO_ROWS_TO_EXPORT_HINT = 'There is no data to download for this search and filters.';
const SEARCH_PLACEHOLDER = 'HR ID, name or mobile';

export function LeaveReportPage() {
  const filters = useLeaveReportFilters();
  const { params, period } = filters;
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(leaveReportListInfiniteQueryOptions(params));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [dateSelection, setDateSelection] = useState<LeaveReportDateSelection | null>(null);

  const columns = useMemo(
    () =>
      getLeaveReportColumns(
        (employee, category) => setDateSelection({ employee, category }),
        params.search,
      ),
    [params.search],
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Leave Report"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={period.dateFrom}
            dateTo={period.dateTo}
          />
        }
        search={{
          value: filters.searchText,
          onChange: filters.setSearchText,
          placeholder: SEARCH_PLACEHOLDER,
        }}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={filters.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <ReportExportButton
              params={params}
              hasNoRows={firstPageMeta?.total === 0}
              isDisabled={isLoading || loadError !== null}
            />
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <LeaveReportFilterPanel filters={filters} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the leave report"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          groupedColumns={LEAVE_REPORT_GROUPED_COLUMNS}
          emptyMessage="No data found"
          emptyHint="Change the search or reset the filters to see employees."
          isRowHighlighted={(employee) => employee.userId === dateSelection?.employee.userId}
          {...loadMoreProps}
        />
      )}

      <LeaveReportDatesDialog
        selection={dateSelection}
        period={period}
        onClose={() => setDateSelection(null)}
      />
    </div>
  );
}

interface ReportExportButtonProps {
  params: LeaveReportParams;
  hasNoRows: boolean;
  isDisabled: boolean;
}

function ReportExportButton({ params, hasNoRows, isDisabled }: ReportExportButtonProps) {
  const exportButton = (
    <ExportButton onExport={() => downloadLeaveReport(params)} disabled={isDisabled || hasNoRows} />
  );

  if (!hasNoRows) return exportButton;

  return (
    <DisabledHint hint={NO_ROWS_TO_EXPORT_HINT} ariaLabel={EXPORT_ARIA_LABEL}>
      {exportButton}
    </DisabledHint>
  );
}
